import { logError } from '@/lib/logError';

// Tolt affiliate tracking. The script records the click when someone arrives on
// an affiliate link and exposes the click id as `window.tolt_referral`; we hand
// that id to dabble-rest on checkout, which stamps it into the Stripe
// subscription's metadata (`tolt_referral`) so Tolt can credit the affiliate.
//
// Runs alongside Rewardful during the transition; the two are independent.
const SCRIPT_ID = 'tolt-js';
const SCRIPT_SRC = 'https://files.tlt-cdn.com/tlt.js';

/** How long a caller waits on tlt.js before giving up on it. See `@/lib/rewardful`. */
const READY_TIMEOUT_MS = 2000;

declare global {
  interface Window {
    tolt_referral?: string;
  }
}

/** Settles once the click id can be read; resolved by default (no id, SSR). */
let ready = Promise.resolve();

/**
 * Why there might be no click id. Reported with the signup so "nobody arrived on
 * an affiliate link" is distinguishable from "the script never ran", the failure
 * that went unnoticed for two years because nothing recorded it.
 */
export type AffiliateStatus = 'not-loaded' | 'loaded' | 'failed' | 'timed-out';
let status: AffiliateStatus = 'not-loaded';

/** Load outcome of the Tolt script, for the signup attribution event. */
export function getToltStatus(): AffiliateStatus {
  return status;
}

/** Load the Tolt tracking script once. No-op without an id. */
export function loadTolt(toltId: string | undefined) {
  if (!toltId) return;
  if (typeof document === 'undefined' || document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.async = true;
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.setAttribute('data-tolt', toltId);

  // tlt.js publishes `window.tolt_referral` while it executes, so its own load
  // event is the signal. Unlike Rewardful there's no second network round trip to
  // wait on. Reading before then yields undefined on a fast submit.
  ready = new Promise<void>((resolve) => {
    script.onload = () => {
      status = 'loaded';
      resolve();
    };
    setTimeout(() => {
      if (status === 'not-loaded') status = 'timed-out';
      resolve();
    }, READY_TIMEOUT_MS);
    script.onerror = () => {
      status = 'failed';
      logError('Tolt script failed to load; conversions will go unattributed', SCRIPT_SRC);
      resolve();
    };
  });

  document.head.appendChild(script);
}

/**
 * The current affiliate click id, or undefined. Waits for tlt.js to publish it
 * (bounded by `READY_TIMEOUT_MS`); an unattributed visit simply has no referral,
 * and every caller omits the field rather than sending an empty one.
 */
export async function getToltReferral(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;
  await ready;
  const referral = window.tolt_referral;
  return typeof referral === 'string' && referral.trim() ? referral.trim() : undefined;
}

/**
 * Spreadable request-body fragment, so a visit with no affiliate referral sends
 * no field at all rather than an empty one Tolt could never match.
 */
export async function toltReferralBody(): Promise<{ tolt_referral?: string }> {
  const referral = await getToltReferral();
  return referral ? { tolt_referral: referral } : {};
}

/**
 * Same value as `toltReferralBody` for a request whose body is otherwise empty:
 * `.send({})` and `.send(undefined)` differ on the wire, and an unattributed
 * signup must post exactly the empty request it always has.
 */
export async function toltReferralPayload(): Promise<{ tolt_referral: string } | undefined> {
  const referral = await getToltReferral();
  return referral ? { tolt_referral: referral } : undefined;
}

/** Test seam: drop the pending wait and load state between cases. */
export function resetToltForTest() {
  ready = Promise.resolve();
  status = 'not-loaded';
}
